import type { PermissionDataType } from "@clovis/server/src/app/domain/utils/permissions/permissions";
import {
  getOrgData,
  getProjectData,
} from "@clovis/server/src/app/domain/utils/permissions/permissions";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/solid";
import { sortBy, uniqBy } from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import tw from "twin.macro";
import { styled } from "~/config/stitches";
import {
  getI18nSection,
  getSectionPriority,
} from "~/config/users-rights-permissions/utils";
import { Box } from "~/design-system/Box";
import { Heading } from "~/design-system/Heading";
import { useBreakpoint } from "~/design-system/hooks";
import { Icon } from "~/design-system/Icon";
import { NavSelect } from "~/design-system/NavSelect";
import { Text } from "~/design-system/Text";
import type { LanguageType } from "~/screens/App/screens/AnonRightsScreen/components/LanguageDropdown/LanguageDropdown";
import { PermissionTableDesktop } from "~/screens/App/screens/AnonRightsScreen/components/PermissionTable/components/PermissionTableDesktop/PermissionTableDesktop";
import { PermissionTableMobile } from "~/screens/App/screens/AnonRightsScreen/components/PermissionTable/components/PermissionTableMobile/PermissionTableMobile";
import { sectionBadgesColors } from "~/screens/App/screens/AnonRightsScreen/components/PermissionTable/components/SectionTranslation";
import { createComponentHook } from "~/types";

type RightsDetailPageProps = {
  type: "org" | "project";
  language: LanguageType;
};

const TabsContainer = styled("div", {
  ...tw`flex mb-4`,

  defaultVariants: {
    isMobile: false,
  },

  variants: {
    isMobile: {
      false: tw``,
      true: tw`justify-center`,
    },
  },
});

const FilterTab = styled("button", {
  ...tw`px-6 py-3 text-base font-medium rounded-t-lg`,

  defaultVariants: {
    active: false,
    isMobile: false,
  },

  variants: {
    active: {
      false: tw`text-gray-600 hover:text-gray-700 hover:bg-gray-100`,
      true: tw`bg-gray-200 text-gray-700`,
    },
    isMobile: {
      false: tw`mr-4`,
      true: tw`mx-1`,
    },
  },
});

const TabsWrapper = styled("div", {
  marginBottom: "$space$1",
  marginTop: "$space$1",
  width: "100%",
});

const MobileSelectWrapper = styled("div", {
  paddingBottom: "$space$2",
  paddingTop: "$space$1",
  position: "sticky",
  top: "0",
  width: "100%",
  zIndex: "10",
});

const NavigationContainer = styled("div", {
  ...tw`flex justify-between w-full mb-4`,
  alignItems: "center",

  defaultVariants: {
    isMobile: false,
  },

  variants: {
    isMobile: {
      false: tw``,
      true: {
        ...tw`flex-col gap-4`,
        alignItems: "stretch",
      },
    },
  },
});

const useRightsDetailPage = createComponentHook(
  (props: RightsDetailPageProps) => {
    const { i18n, t } = useTranslation();
    const { breakpoint } = useBreakpoint();
    const location = useLocation();
    const navigate = useNavigate();
    const [activeSection, setActiveSection] = React.useState<string | null>(
      null
    );
    const [hasUserMadeChoice, setHasUserMadeChoice] = React.useState(false);

    React.useEffect(() => {
      void i18n.changeLanguage(props.language);
    }, [props.language, i18n]);

    const isMobile = breakpoint === "mobile" || breakpoint === "tablet";

    // Get computed title and subtitle based on type
    const title =
      props.type === "org"
        ? t(
            "screens.Rights.components.RightsDetailPage.orgRights",
            "Organization Rights"
          )
        : t(
            "screens.Rights.components.RightsDetailPage.projectRights",
            "Project Rights"
          );

    const subtitle = t(
      "screens.Rights.components.RightsDetailPage.rolesAndPermissions",
      "Roles and permissions"
    );

    // Get all permissions based on type
    const permissions: PermissionDataType[] =
      props.type === "org" ? getOrgData() : getProjectData();

    // Extract unique sections and sort them by priority
    const sections = sortBy(
      uniqBy(
        permissions.map((p) => ({
          priority: getSectionPriority(p.section) ?? 99,
          section: p.section,
        })),
        "section"
      ),
      "priority"
    );

    // Filter permissions by active section if set
    const filteredPermissions: PermissionDataType[] = activeSection
      ? permissions.filter((p) => p.section === activeSection)
      : permissions;

    // Handle navigation and URL changes
    React.useEffect(() => {
      const hash = location.hash.substring(1);
      const searchParams = new URLSearchParams(location.search);
      const sectionParam = searchParams.get("section");

      // For mobile, prioritize query params; for desktop, prioritize hash
      if (
        isMobile &&
        sectionParam &&
        sections.some((s) => s.section === sectionParam)
      ) {
        setActiveSection(sectionParam);
        setHasUserMadeChoice(true);
      } else if (
        !isMobile &&
        hash &&
        sections.some((s) => s.section === hash)
      ) {
        setActiveSection(hash);
        setHasUserMadeChoice(true);
      } else if (!sectionParam && !hash) {
        // When on base URL without any section parameters, show all sections
        setActiveSection(null);
        setHasUserMadeChoice(true);
      } else if (!hasUserMadeChoice) {
        // Default to "All Sections" on initial load
        setActiveSection(null);
        setHasUserMadeChoice(true);
      }
    }, [location.hash, location.search, sections, hasUserMadeChoice, isMobile]);

    // Handle changing the active section
    const handleSectionClick = (section: string) => {
      setActiveSection(section);
      setHasUserMadeChoice(true);
      if (isMobile) {
        // For mobile, always use query params
        const routePath = props.type === "org" ? "organization" : "project";
        navigate(`/rights/${routePath}?section=${section}`, { replace: true });
      } else {
        // For desktop, use hash
        navigate(`#${section}`, { replace: true });
      }
    };

    // Handle "All" tab
    const handleAllClick = () => {
      setActiveSection(null);
      setHasUserMadeChoice(true);
      const routePath = props.type === "org" ? "organization" : "project";
      navigate(`/rights/${routePath}`, { replace: true });
    };

    // Get info for the other rights type
    const otherType = props.type === "org" ? "project" : "organization";
    const otherTypeTitle =
      props.type === "org"
        ? t(
            "screens.Rights.components.RightsDetailPage.projectRights",
            "Project Rights"
          )
        : t(
            "screens.Rights.components.RightsDetailPage.orgRights",
            "Organization Rights"
          );

    // Create options for NavSelect - use full URLs that include current query state
    const navSelectOptions = React.useMemo(() => {
      const routePath = props.type === "org" ? "organization" : "project";
      return [
        {
          children: t(
            "screens.Rights.components.RightsDetailPage.allSections",
            "All Sections"
          ),
          to: `/rights/${routePath}`,
        },
        ...sections.map((section) => ({
          children: getI18nSection(section.section, t) ?? section.section,
          to: `/rights/${routePath}?section=${section.section}`,
        })),
      ];
    }, [props.type, sections, t]);

    // Force NavSelect to use the correct active option by checking current state
    const currentNavOption = React.useMemo(() => {
      if (!activeSection) {
        return navSelectOptions[0]; // "All Sections"
      }
      return (
        navSelectOptions.find((option) =>
          option.to.includes(`section=${activeSection}`)
        ) ?? navSelectOptions[0]
      );
    }, [activeSection, navSelectOptions]);

    return {
      actions: {
        handleAllClick,
        handleSectionClick,
      },
      state: {
        activeSection,
        currentNavOption,
        filteredPermissions,
        isMobile,
        navSelectOptions,
        otherType,
        otherTypeTitle,
        sections,
        subtitle,
        title,
      },
      t,
    };
  }
);

function RightsDetailPage(props: RightsDetailPageProps) {
  const { actions, state, t } = useRightsDetailPage(props);

  return (
    <Box css={{ marginTop: "$space$4" }}>
      <NavigationContainer isMobile={state.isMobile}>
        {/* Mobile: Navigation buttons row */}
        {state.isMobile && (
          <Box
            css={{
              alignItems: "center",
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Link to="/rights">
              <Box
                css={{
                  "&:hover": {
                    textDecoration: "underline",
                  },
                  alignItems: "center",
                  color: "$colors$primary600",
                  display: "flex",
                  textDecoration: "none",
                }}
              >
                <Icon size="small" label="back">
                  <ChevronLeftIcon />
                </Icon>
                <Box css={{ marginLeft: "$space$2" }}>
                  {t(
                    "screens.Rights.components.RightsDetailPage.backToRightsMobile",
                    "Rights"
                  )}
                </Box>
              </Box>
            </Link>

            <Link to={`/rights/${state.otherType}`}>
              <Box
                css={{
                  "&:hover": {
                    textDecoration: "underline",
                  },
                  alignItems: "center",
                  color: "$colors$primary600",
                  display: "flex",
                  textDecoration: "none",
                }}
              >
                <Box css={{ marginRight: "$space$2" }}>
                  {props.type === "org"
                    ? t(
                        "screens.Rights.components.RightsDetailPage.projectRights",
                        "Project Rights"
                      )
                    : t(
                        "screens.Rights.components.RightsDetailPage.orgRights",
                        "Organization Rights"
                      )}
                </Box>
                <Icon size="small" label="next">
                  <ChevronRightIcon />
                </Icon>
              </Box>
            </Link>
          </Box>
        )}

        {/* Desktop: Original layout */}
        {!state.isMobile && (
          <>
            <Box css={{ alignSelf: "flex-start", marginTop: "$space$1" }}>
              <Link to="/rights">
                <Box
                  css={{
                    "&:hover": {
                      textDecoration: "underline",
                    },
                    alignItems: "center",
                    color: "$colors$primary600",
                    display: "flex",
                    textDecoration: "none",
                  }}
                >
                  <Icon size="small" label="back">
                    <ChevronLeftIcon />
                  </Icon>
                  <Box css={{ marginLeft: "$space$2" }}>
                    {t(
                      "screens.Rights.components.RightsDetailPage.backToRights",
                      "Back to Rights"
                    )}
                  </Box>
                </Box>
              </Link>
            </Box>

            {/* Title in the center */}
            <Box css={{ flex: 1, textAlign: "center" }}>
              <Heading as="h2" level="2" css={{ marginBottom: "$space$1" }}>
                {state.title}
              </Heading>
              <Text
                size="standard"
                tone="secondary"
                css={{ fontSize: "$fontSizes$sm" }}
              >
                {state.subtitle}
              </Text>
            </Box>

            <Box css={{ alignSelf: "flex-start", marginTop: "$space$1" }}>
              <Link to={`/rights/${state.otherType}`}>
                <Box
                  css={{
                    "&:hover": {
                      textDecoration: "underline",
                    },
                    alignItems: "center",
                    color: "$colors$primary600",
                    display: "flex",
                    textDecoration: "none",
                  }}
                >
                  <Box css={{ marginRight: "$space$2" }}>
                    {state.otherTypeTitle}
                  </Box>
                  <Icon size="small" label="next">
                    <ChevronRightIcon />
                  </Icon>
                </Box>
              </Link>
            </Box>
          </>
        )}

        {/* Mobile: Title centered below navigation */}
        {state.isMobile && (
          <Box css={{ textAlign: "center", width: "100%" }}>
            <Heading as="h2" level="2" css={{ marginBottom: "$space$1" }}>
              {state.title}
            </Heading>
            <Text
              size="standard"
              tone="secondary"
              css={{ fontSize: "$fontSizes$sm" }}
            >
              {state.subtitle}
            </Text>
          </Box>
        )}
      </NavigationContainer>

      {state.isMobile ? (
        <MobileSelectWrapper>
          <NavSelect
            options={state.navSelectOptions}
            value={state.currentNavOption}
            rounded
            centered
          />
        </MobileSelectWrapper>
      ) : (
        /* Desktop Tabs */
        <TabsWrapper>
          <TabsContainer isMobile={state.isMobile}>
            {/* All Sections Tab */}
            <FilterTab
              active={state.activeSection === null}
              isMobile={state.isMobile}
              onClick={actions.handleAllClick}
            >
              {t(
                "screens.Rights.components.RightsDetailPage.allSections",
                "All Sections"
              )}
            </FilterTab>

            {/* Section Tabs */}
            {state.sections.map((section) => {
              const isActive = state.activeSection === section.section;
              const sectionColor = sectionBadgesColors[section.section];

              return (
                <FilterTab
                  key={section.section}
                  active={isActive}
                  isMobile={state.isMobile}
                  onClick={() => actions.handleSectionClick(section.section)}
                  css={
                    isActive && sectionColor
                      ? {
                          backgroundColor: `$colors$${sectionColor}100`,
                          borderBottomColor: `$colors$${sectionColor}500`,
                          borderColor: `$colors$${sectionColor}300`,
                          color: `$colors$${sectionColor}700`,
                        }
                      : undefined
                  }
                >
                  {getI18nSection(section.section, t) ?? section.section}
                </FilterTab>
              );
            })}
          </TabsContainer>
        </TabsWrapper>
      )}

      {state.isMobile ? (
        <PermissionTableMobile
          type={props.type}
          language={props.language}
          filteredPermissions={state.filteredPermissions}
        />
      ) : (
        <PermissionTableDesktop
          type={props.type}
          language={props.language}
          filteredPermissions={state.filteredPermissions}
        />
      )}
    </Box>
  );
}

export { RightsDetailPage };
